import { useEffect, useState } from "react";
import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Stack,
  Button,
  NumberInput,
  Select,
  TextInput,
  Alert,
  Divider,
  Badge,
  Loader,
  ActionIcon,
  Paper,
  Switch,
} from "@mantine/core";
import { IconAdjustments, IconDeviceMobile, IconMusic, IconCoin, IconShieldCheck, IconLock, IconMessageQuestion, IconTrash, IconPlus, IconSnowflake, IconChartBar } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import axios from "axios";
import { Endpoints } from "../../../lib/endpoints";
import { useAuth } from "../../../hooks/useAuth";

// Only these people see the Control Center (matches the nav gate).
const ALLOW = ["anointedexcel59@gmail.com", "fasanfavour123@gmail.com"];

const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("t")}` },
});

async function loadConfig(key: string): Promise<any> {
  try {
    const r = await axios.get(Endpoints.GetConfigByKey(key).route, authHeaders());
    return r.data?.data?.value ?? r.data?.value ?? r.data?.data ?? null;
  } catch {
    return null;
  }
}
async function saveConfig(key: string, value: any, category = "control") {
  // Upsert: PATCH if it exists, else POST to create.
  try {
    await axios.patch(Endpoints.UpdateConfig(key).route, { value }, authHeaders());
  } catch {
    await axios.post(
      Endpoints.CreateConfig().route,
      { key, value, category },
      authHeaders(),
    );
  }
}

/** Section: Survey questions (Config "survey_questions") — edit the in-app questions. */
type SurveyQ = { key: string; prompt: string; options: { label: string; value: string }[] };
function SurveyQuestions() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [qs, setQs] = useState<SurveyQ[]>([]);

  useEffect(() => {
    // Load the LIVE set from the same public endpoint the app uses — always
    // populated (falls back to defaults server-side), so the editor pre-fills.
    axios
      .get(Endpoints.GetConfigByKey("public/survey-questions").route, authHeaders())
      .then((r) => {
        const v = r.data?.data?.questions ?? r.data?.questions;
        if (Array.isArray(v) && v.length) setQs(v);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const setQ = (i: number, patch: Partial<SurveyQ>) =>
    setQs((cur) => cur.map((q, idx) => (idx === i ? { ...q, ...patch } : q)));
  const setOpt = (qi: number, oi: number, patch: Partial<{ label: string; value: string }>) =>
    setQs((cur) =>
      cur.map((q, idx) =>
        idx === qi
          ? { ...q, options: q.options.map((o, j) => (j === oi ? { ...o, ...patch } : o)) }
          : q,
      ),
    );
  const addQ = () =>
    setQs((cur) => [...cur, { key: "", prompt: "", options: [{ label: "", value: "" }] }]);
  const removeQ = (i: number) => setQs((cur) => cur.filter((_, idx) => idx !== i));
  const addOpt = (qi: number) =>
    setQs((cur) =>
      cur.map((q, idx) => (idx === qi ? { ...q, options: [...q.options, { label: "", value: "" }] } : q)),
    );
  const removeOpt = (qi: number, oi: number) =>
    setQs((cur) =>
      cur.map((q, idx) => (idx === qi ? { ...q, options: q.options.filter((_, j) => j !== oi) } : q)),
    );

  const save = async () => {
    // Validate: every question needs a key, prompt, and ≥2 complete options.
    for (const q of qs) {
      if (!q.key.trim() || !q.prompt.trim())
        return notifications.show({ title: "Missing", message: "Every question needs a key and prompt.", color: "red" });
      const good = q.options.filter((o) => o.label.trim() && o.value.trim());
      if (good.length < 2)
        return notifications.show({ title: "Options", message: `"${q.key || "?"}" needs at least 2 complete options.`, color: "red" });
    }
    const keys = qs.map((q) => q.key.trim());
    if (new Set(keys).size !== keys.length)
      return notifications.show({ title: "Duplicate", message: "Question keys must be unique.", color: "red" });

    setSaving(true);
    try {
      const clean = qs.map((q) => ({
        key: q.key.trim(),
        prompt: q.prompt.trim(),
        options: q.options
          .filter((o) => o.label.trim() && o.value.trim())
          .map((o) => ({ label: o.label.trim(), value: o.value.trim() })),
      }));
      await saveConfig("survey_questions", clean, "survey");
      notifications.show({ title: "Saved", message: "Questions updated — live in the app on next load", color: "green" });
    } catch (e: any) {
      notifications.show({ title: "Error", message: e?.response?.data?.message || e.message, color: "red" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card withBorder radius="md" p="lg">
      <Group gap="xs" mb="xs">
        <IconMessageQuestion size={20} color="var(--mantine-color-green-6)" />
        <Title order={4}>In-app questions</Title>
        <Badge color="orange" variant="light">survey</Badge>
      </Group>
      <Text c="dimmed" size="sm" mb="md">
        The questions members answer after listening (no money; the answer shapes what they see next).
        Edit here — they update in the app with no store release. Each question needs a unique key and
        2+ options.
      </Text>
      {loading ? (
        <Loader size="sm" />
      ) : (
        <Stack gap="md">
          {qs.map((q, qi) => (
            <Paper key={qi} withBorder p="sm" radius="sm">
              <Group justify="space-between" mb="xs">
                <Text size="xs" c="dimmed" fw={600}>Question {qi + 1}</Text>
                <ActionIcon color="red" variant="subtle" onClick={() => removeQ(qi)} title="Remove question">
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
              <Group grow mb="xs">
                <TextInput label="Key (internal id)" placeholder="reaction" value={q.key} onChange={(e) => setQ(qi, { key: e.currentTarget.value })} />
              </Group>
              <TextInput label="Prompt (shown to member)" placeholder="Did you enjoy this song?" value={q.prompt} onChange={(e) => setQ(qi, { prompt: e.currentTarget.value })} mb="xs" />
              <Text size="xs" c="dimmed" mb={4}>Options</Text>
              <Stack gap={6}>
                {q.options.map((o, oi) => (
                  <Group key={oi} gap={6} wrap="nowrap">
                    <TextInput placeholder="Label (e.g. Yes, I liked it)" value={o.label} onChange={(e) => setOpt(qi, oi, { label: e.currentTarget.value })} style={{ flex: 2 }} />
                    <TextInput placeholder="value (e.g. like)" value={o.value} onChange={(e) => setOpt(qi, oi, { value: e.currentTarget.value })} style={{ flex: 1 }} />
                    <ActionIcon color="red" variant="subtle" onClick={() => removeOpt(qi, oi)} title="Remove option">
                      <IconTrash size={14} />
                    </ActionIcon>
                  </Group>
                ))}
                <Button size="xs" variant="light" leftSection={<IconPlus size={12} />} onClick={() => addOpt(qi)} style={{ alignSelf: "flex-start" }}>
                  Add option
                </Button>
              </Stack>
            </Paper>
          ))}
          <Group justify="space-between">
            <Button variant="light" leftSection={<IconPlus size={14} />} onClick={addQ}>Add question</Button>
            <Button loading={saving} onClick={save}>Save questions</Button>
          </Group>
        </Stack>
      )}
    </Card>
  );
}

/** Section: Frozen accounts — see fraud-frozen accounts, unfreeze or refund. */
type FrozenAcct = { id: string; email: string; name: string; balance: number; bank: string; reason: string; clawedBack: number; restorable: number };
function FrozenAccounts() {
  const [loading, setLoading] = useState(true);
  const [accts, setAccts] = useState<FrozenAcct[]>([]);
  const [busy, setBusy] = useState<string>("");
  const [search, setSearch] = useState("");

  const load = () => {
    setLoading(true);
    axios
      .get(Endpoints.GetFrozenAccounts().route + (search ? `?search=${encodeURIComponent(search)}` : ""), authHeaders())
      .then((r) => setAccts(r.data?.data?.accounts || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (a: FrozenAcct, kind: "unfreeze" | "refund") => {
    const label = kind === "refund" ? `Refund ₦${a.restorable} to ${a.email} and unfreeze them?` : `Unfreeze ${a.email}? They'll be able to withdraw and earn again.`;
    if (!window.confirm(label)) return;
    setBusy(a.id + kind);
    try {
      const ep = kind === "refund" ? Endpoints.RefundAccount(a.id) : Endpoints.UnfreezeAccount(a.id);
      const r = await axios.post(ep.route, {}, authHeaders());
      notifications.show({ title: "Done", message: r.data?.message || "Done", color: "green" });
      load();
    } catch (e: any) {
      notifications.show({ title: "Error", message: e?.response?.data?.message || e.message, color: "red" });
    } finally {
      setBusy("");
    }
  };

  return (
    <Card withBorder radius="md" p="lg">
      <Group gap="xs" mb="xs">
        <IconSnowflake size={20} color="var(--mantine-color-blue-6)" />
        <Title order={4}>Frozen accounts</Title>
        <Badge color="blue" variant="light">{accts.length}</Badge>
      </Group>
      <Text c="dimmed" size="sm" mb="md">
        Accounts frozen for fraud. <b>Unfreeze</b> lets them withdraw/earn again; <b>Refund</b> credits
        back the money that was removed (recorded &amp; reversible) and unfreezes them. Use for genuine appeals.
      </Text>
      <Group mb="sm">
        <TextInput placeholder="Search email/name" value={search} onChange={(e) => setSearch(e.currentTarget.value)} onKeyDown={(e) => e.key === "Enter" && load()} style={{ flex: 1, maxWidth: 320 }} />
        <Button variant="light" onClick={load}>Search</Button>
      </Group>
      {loading ? (
        <Loader size="sm" />
      ) : accts.length === 0 ? (
        <Text c="dimmed" size="sm">No frozen accounts.</Text>
      ) : (
        <Stack gap="xs">
          {accts.map((a) => (
            <Paper key={a.id} withBorder p="sm" radius="sm">
              <Group justify="space-between" wrap="nowrap">
                <div style={{ minWidth: 0 }}>
                  <Text size="sm" fw={600} lineClamp={1}>{a.email} {a.name ? `· ${a.name}` : ""}</Text>
                  <Text size="xs" c="dimmed" lineClamp={1}>{a.bank}</Text>
                  <Group gap={6} mt={4}>
                    <Badge size="xs" color="red" variant="light">{a.reason}</Badge>
                    <Badge size="xs" color="gray" variant="light">balance ₦{a.balance}</Badge>
                    {a.restorable > 0 ? <Badge size="xs" color="orange" variant="light">removed ₦{a.restorable}</Badge> : null}
                  </Group>
                </div>
                <Group gap={6} wrap="nowrap">
                  <Button size="xs" variant="light" loading={busy === a.id + "unfreeze"} onClick={() => act(a, "unfreeze")}>Unfreeze</Button>
                  {a.restorable > 0 ? (
                    <Button size="xs" color="orange" loading={busy === a.id + "refund"} onClick={() => act(a, "refund")}>Refund ₦{a.restorable}</Button>
                  ) : null}
                </Group>
              </Group>
            </Paper>
          ))}
        </Stack>
      )}
    </Card>
  );
}

/** Section: Enforcement modes (Config "enforcement_modes") — the 3 protection switches. */
function EnforcementModes() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tierAccess, setTierAccess] = useState<string>("enforce");
  const [socialVerify, setSocialVerify] = useState<string>("enforce");
  const [playTime, setPlayTime] = useState<string>("shadow");

  useEffect(() => {
    loadConfig("enforcement_modes").then((v) => {
      if (v) {
        if (v.tierAccess) setTierAccess(v.tierAccess);
        if (v.socialVerify) setSocialVerify(v.socialVerify);
        if (v.playTime) setPlayTime(v.playTime);
      }
      setLoading(false);
    });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await saveConfig("enforcement_modes", { tierAccess, socialVerify, playTime }, "guardrails");
      notifications.show({ title: "Saved", message: "Protection modes updated — live within ~1 min", color: "green" });
    } catch (e: any) {
      notifications.show({ title: "Error", message: e?.response?.data?.message || e.message, color: "red" });
    } finally {
      setSaving(false);
    }
  };

  const modeData = [
    { value: "enforce", label: "Enforce — actively blocks" },
    { value: "shadow", label: "Shadow — logs only, blocks nobody" },
    { value: "off", label: "Off — does nothing" },
  ];
  const badgeColor = (v: string) => (v === "enforce" ? "green" : v === "shadow" ? "yellow" : "gray");

  return (
    <Card withBorder radius="md" p="lg">
      <Group gap="xs" mb="xs">
        <IconLock size={20} color="var(--mantine-color-green-6)" />
        <Title order={4}>Protection switches</Title>
        <Badge color="orange" variant="light">guardrails</Badge>
      </Group>
      <Text c="dimmed" size="sm" mb="md">
        The three fraud protections. Set to <b>Enforce</b> to actually block. These persist here —
        they can no longer silently turn off on a redeploy. Changes go live within ~1 minute.
      </Text>
      {loading ? (
        <Loader size="sm" />
      ) : (
        <Stack gap="sm">
          <Select
            label={<Group gap={6}><span>Tier access</span><Badge size="xs" color={badgeColor(tierAccess)}>{tierAccess}</Badge></Group>}
            description="Blocks unverified accounts from engagement opportunities"
            data={modeData}
            value={tierAccess}
            onChange={(v) => setTierAccess(v || "enforce")}
          />
          <Select
            label={<Group gap={6}><span>Social verify</span><Badge size="xs" color={badgeColor(socialVerify)}>{socialVerify}</Badge></Group>}
            description="Verification that catches fake / doctored accounts"
            data={modeData}
            value={socialVerify}
            onChange={(v) => setSocialVerify(v || "enforce")}
          />
          <Select
            label={<Group gap={6}><span>Play-time check</span><Badge size="xs" color={badgeColor(playTime)}>{playTime}</Badge></Group>}
            description="Confirms a listen was a real 30s+ stream (fail-open)"
            data={modeData}
            value={playTime}
            onChange={(v) => setPlayTime(v || "shadow")}
          />
          <Group justify="flex-end">
            <Button loading={saving} onClick={save}>Save</Button>
          </Group>
        </Stack>
      )}
    </Card>
  );
}

/** Section: Verification standards (Config "verification_thresholds") — tune the fraud bar. */
function VerificationStandards() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [minF, setMinF] = useState<number | string>(15);
  const [minFX, setMinFX] = useState<number | string>(5);
  const [minP, setMinP] = useState<number | string>(5);
  const [highFollow, setHighFollow] = useState<number | string>(2000);

  useEffect(() => {
    loadConfig("verification_thresholds").then((v) => {
      if (v) {
        if (v.minFollowersDefault != null) setMinF(v.minFollowersDefault);
        if (v.minFollowersX != null) setMinFX(v.minFollowersX);
        if (v.minPosts != null) setMinP(v.minPosts);
        if (v.highFollowingSignal != null) setHighFollow(v.highFollowingSignal);
      }
      setLoading(false);
    });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await saveConfig(
        "verification_thresholds",
        {
          minFollowersDefault: Number(minF) || 0,
          minFollowersX: Number(minFX) || 0,
          minPosts: Number(minP) || 0,
          highFollowingSignal: Number(highFollow) || 0,
        },
        "guardrails",
      );
      notifications.show({ title: "Saved", message: "Verification standards updated", color: "green" });
    } catch (e: any) {
      notifications.show({ title: "Error", message: e?.response?.data?.message || e.message, color: "red" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card withBorder radius="md" p="lg">
      <Group gap="xs" mb="xs">
        <IconShieldCheck size={20} color="var(--mantine-color-green-6)" />
        <Title order={4}>Verification standards</Title>
        <Badge color="orange" variant="light">guardrails</Badge>
      </Group>
      <Text c="dimmed" size="sm" mb="md">
        The bar an account must clear to be Verified. Raise it to tighten against fake accounts,
        lower to loosen. Applies to new verifications; takes effect immediately, no deploy.
      </Text>
      {loading ? (
        <Loader size="sm" />
      ) : (
        <Stack gap="sm">
          <Group grow>
            <NumberInput label="Min followers (IG / TikTok)" min={0} value={minF} onChange={setMinF} />
            <NumberInput label="Min followers (X / Twitter)" min={0} value={minFX} onChange={setMinFX} />
          </Group>
          <Group grow>
            <NumberInput label="Min posts" min={0} value={minP} onChange={setMinP} />
            <NumberInput
              label="High-following signal"
              description="Following above this with <100 followers → manual review"
              min={0}
              step={100}
              value={highFollow}
              onChange={setHighFollow}
            />
          </Group>
          <Group justify="flex-end">
            <Button loading={saving} onClick={save}>Save</Button>
          </Group>
        </Stack>
      )}
    </Card>
  );
}

/** Section: Fresh-clock window (Config "fresh_clock") — old-screenshot defence. */
function FreshClock() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [back, setBack] = useState<number | string>(75);
  const [fwd, setFwd] = useState<number | string>(10);

  useEffect(() => {
    loadConfig("fresh_clock").then((v) => {
      if (v) {
        if (v.backMinutes != null) setBack(v.backMinutes);
        if (v.forwardMinutes != null) setFwd(v.forwardMinutes);
      }
      setLoading(false);
    });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await saveConfig(
        "fresh_clock",
        { backMinutes: Number(back) || 0, forwardMinutes: Number(fwd) || 0 },
        "guardrails",
      );
      notifications.show({ title: "Saved", message: "Fresh-screenshot window updated", color: "green" });
    } catch (e: any) {
      notifications.show({ title: "Error", message: e?.response?.data?.message || e.message, color: "red" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card withBorder radius="md" p="lg">
      <Group gap="xs" mb="xs">
        <IconLock size={20} color="var(--mantine-color-green-6)" />
        <Title order={4}>Fresh-screenshot window</Title>
        <Badge color="orange" variant="light">guardrails</Badge>
      </Group>
      <Text c="dimmed" size="sm" mb="md">
        How old a listen screenshot's phone clock may be. <b>Lower "minutes old" to fight old
        screenshots</b> — a tighter window is harder to fake. Too tight can false-reject slow uploads;
        75 is loose, ~20–30 is a good tightening. Pair with Play-time = Enforce.
      </Text>
      {loading ? (
        <Loader size="sm" />
      ) : (
        <Group align="flex-end">
          <NumberInput label="Minutes old allowed (back)" min={0} max={180} value={back} onChange={setBack} style={{ flex: 1, maxWidth: 220 }} />
          <NumberInput label="Minutes ahead allowed (clock skew)" min={0} max={60} value={fwd} onChange={setFwd} style={{ flex: 1, maxWidth: 220 }} />
          <Button loading={saving} onClick={save}>Save</Button>
        </Group>
      )}
    </Card>
  );
}

/** Section: Daily reward cap (Config "daily_reward_cap") — variable per-member/day limit. */
function DailyCap() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cap, setCap] = useState<number | string>("");

  useEffect(() => {
    loadConfig("daily_reward_cap").then((v) => {
      if (v != null && v !== "") setCap(Number(v));
      setLoading(false);
    });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      // Blank / 0 = no cap.
      const value = cap === "" || Number(cap) <= 0 ? 0 : Math.floor(Number(cap));
      await saveConfig("daily_reward_cap", value, "economy");
      notifications.show({
        title: "Saved",
        message: value ? `Daily cap set to ₦${value}/member` : "Daily cap removed (no limit)",
        color: "green",
      });
    } catch (e: any) {
      notifications.show({ title: "Error", message: e?.response?.data?.message || e.message, color: "red" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card withBorder radius="md" p="lg">
      <Group gap="xs" mb="xs">
        <IconCoin size={20} color="var(--mantine-color-green-6)" />
        <Title order={4}>Daily reward cap</Title>
        <Badge color="orange" variant="light">economy</Badge>
      </Group>
      <Text c="dimmed" size="sm" mb="md">
        The most a member can earn in one day (WAT). Change it per campaign (₦500, ₦800…). Leave
        blank / 0 for no cap. Once a member hits it, further completions pay ₦0 until tomorrow.
      </Text>
      {loading ? (
        <Loader size="sm" />
      ) : (
        <Group align="flex-end">
          <NumberInput
            label="Today's cap (₦ per member)"
            placeholder="e.g. 500 — leave blank for no cap"
            min={0}
            step={50}
            value={cap}
            onChange={setCap}
            style={{ flex: 1, maxWidth: 320 }}
          />
          <Button loading={saving} onClick={save}>Save</Button>
        </Group>
      )}
    </Card>
  );
}

/** Section: Listening slots (Config "listen_slot") — drives how many songs open at once. */
function ListeningSlots() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [songsAtOnce, setSongsAtOnce] = useState<number | string>(3);
  const [gapMinutes, setGapMinutes] = useState<number | string>(10);
  const [gapMode, setGapMode] = useState<string>("global");

  useEffect(() => {
    loadConfig("listen_slot").then((v) => {
      if (v) {
        if (v.songsAtOnce != null) setSongsAtOnce(v.songsAtOnce);
        if (v.gapMinutes != null) setGapMinutes(v.gapMinutes);
        if (v.gapMode) setGapMode(v.gapMode);
      }
      setLoading(false);
    });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await saveConfig("listen_slot", {
        songsAtOnce: Number(songsAtOnce) || 1,
        gapMinutes: Number(gapMinutes) || 0,
        gapMode: gapMode === "per_song" ? "per_song" : "global",
      });
      notifications.show({ title: "Saved", message: "Listening slots updated", color: "green" });
    } catch (e: any) {
      notifications.show({ title: "Error", message: e?.response?.data?.message || e.message, color: "red" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card withBorder radius="md" p="lg">
      <Group gap="xs" mb="xs">
        <IconMusic size={20} color="var(--mantine-color-green-6)" />
        <Title order={4}>Listening slots</Title>
      </Group>
      <Text c="dimmed" size="sm" mb="md">
        How members receive song plays. Defaults keep a single-song day unchanged.
      </Text>
      {loading ? (
        <Loader size="sm" />
      ) : (
        <Stack gap="sm">
          <NumberInput
            label="Songs open at once"
            description="How many DIFFERENT songs a member can start immediately (then the gap applies). Boss's '3 at once'."
            min={1}
            max={10}
            value={songsAtOnce}
            onChange={setSongsAtOnce}
          />
          <NumberInput
            label="Gap between plays (minutes)"
            description="Wait after a play before the next one unlocks. A repeat of the same song always waits this long."
            min={0}
            max={120}
            value={gapMinutes}
            onChange={setGapMinutes}
          />
          <Select
            label="Gap mode"
            description="Global = one play every X min overall. Per-song = each song's next play is X min after that song's last play."
            data={[
              { value: "global", label: "Global — one play every X minutes" },
              { value: "per_song", label: "Per-song — X minutes per song" },
            ]}
            value={gapMode}
            onChange={(v) => setGapMode(v || "global")}
          />
          <Group justify="flex-end">
            <Button loading={saving} onClick={save}>Save</Button>
          </Group>
        </Stack>
      )}
    </Card>
  );
}

/** Section: App version / update nudge (Config "mobile_app_version"). */
function AppVersion() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [v, setV] = useState<any>({
    android: { latestVersion: "", minVersion: "", url: "" },
    ios: { latestVersion: "", minVersion: "", url: "" },
  });

  useEffect(() => {
    loadConfig("mobile_app_version").then((val) => {
      if (val?.android || val?.ios) setV(val);
      setLoading(false);
    });
  }, []);

  const set = (plat: "android" | "ios", field: string, value: string) =>
    setV((cur: any) => ({ ...cur, [plat]: { ...cur[plat], [field]: value } }));

  const save = async () => {
    setSaving(true);
    try {
      await saveConfig("mobile_app_version", v, "mobile");
      notifications.show({ title: "Saved", message: "App version updated — old builds get nudged", color: "green" });
    } catch (e: any) {
      notifications.show({ title: "Error", message: e?.response?.data?.message || e.message, color: "red" });
    } finally {
      setSaving(false);
    }
  };

  const platBlock = (plat: "android" | "ios", label: string) => (
    <Stack gap={8}>
      <Text fw={600} size="sm">{label}</Text>
      <Group grow>
        <TextInput label="Latest version" placeholder="1.0.6" value={v[plat]?.latestVersion || ""} onChange={(e) => set(plat, "latestVersion", e.currentTarget.value)} />
        <TextInput label="Min version (force)" placeholder="1.0.0" value={v[plat]?.minVersion || ""} onChange={(e) => set(plat, "minVersion", e.currentTarget.value)} />
      </Group>
      <TextInput label="Store URL" value={v[plat]?.url || ""} onChange={(e) => set(plat, "url", e.currentTarget.value)} />
    </Stack>
  );

  return (
    <Card withBorder radius="md" p="lg">
      <Group gap="xs" mb="xs">
        <IconDeviceMobile size={20} color="var(--mantine-color-green-6)" />
        <Title order={4}>App version & update nudge</Title>
      </Group>
      <Text c="dimmed" size="sm" mb="md">
        Set <b>latest</b> to soft-nudge older builds; raise <b>min</b> to force an update. Leave min low to avoid locking anyone out.
      </Text>
      {loading ? (
        <Loader size="sm" />
      ) : (
        <Stack gap="md">
          {platBlock("android", "Android")}
          <Divider />
          {platBlock("ios", "iOS")}
          <Group justify="flex-end">
            <Button loading={saving} onClick={save}>Save</Button>
          </Group>
        </Stack>
      )}
    </Card>
  );
}

/** Section: Song survey insights — read-only collation of members' in-app answers. */
type SI_Answer = { value: string; count: number; pct: number };
type SI_Question = { key: string; total: number; answers: SI_Answer[] };
type SI_Song = { songKey: string; label: string; respondents: number; responses: number; questions: SI_Question[] };

const SI_Q_LABEL: Record<string, string> = {
  reaction: "Reaction to the song",
  return_listener: "Listened again on their own?",
  voluntary_conversion: "Saved / followed on their own?",
  explored_another: "Explored another song?",
};
const SI_A_LABEL: Record<string, string> = {
  like: "Liked it", okay: "It's okay", dislike: "Not for me",
  yes: "Yes", no: "Not yet", saved: "Saved it", followed: "Followed artist",
  playlist: "Added to playlist", none: "Not yet",
};
const SI_A_COLOR = (v: string): string =>
  ["like", "yes", "saved", "followed", "playlist"].includes(v) ? "green" : v === "okay" ? "yellow" : "gray";

function SurveyInsights() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<{ songs: SI_Song[]; totalRespondents: number; totalResponses: number } | null>(null);

  const load = () => {
    setLoading(true);
    axios
      .get(Endpoints.GetSurveyInsights().route, authHeaders())
      .then((r) => setData(r.data?.data || null))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Card withBorder radius="md" p="lg">
      <Group gap="xs" mb="xs">
        <IconChartBar size={20} color="var(--mantine-color-grape-6)" />
        <Title order={4}>Song survey insights</Title>
        {data ? <Badge color="grape" variant="light">{data.totalRespondents} people · {data.totalResponses} answers</Badge> : null}
        <Button size="xs" variant="subtle" onClick={load} ml="auto">Refresh</Button>
      </Group>
      <Text c="dimmed" size="sm" mb="md">
        What members answered in the in-app song survey after listening — how they reacted, whether they
        came back to the song on their own, and any voluntary saves/follows. Read-only insight, per song.
      </Text>
      {loading ? (
        <Loader size="sm" />
      ) : !data || data.songs.length === 0 ? (
        <Text c="dimmed" size="sm">No survey answers yet.</Text>
      ) : (
        <Stack gap="md">
          {data.songs.map((s) => (
            <Paper key={s.songKey} withBorder p="sm" radius="sm">
              <Group justify="space-between" mb={6} wrap="nowrap">
                <Text size="sm" fw={600} lineClamp={1}>{s.label}</Text>
                <Badge size="xs" variant="light" color="gray">{s.respondents} respondents</Badge>
              </Group>
              <Stack gap={8}>
                {s.questions.map((q) => (
                  <div key={q.key}>
                    <Text size="xs" c="dimmed" mb={4}>{SI_Q_LABEL[q.key] || q.key}</Text>
                    <Group gap={6} wrap="wrap">
                      {q.answers.map((a) => (
                        <Badge key={a.value} size="sm" variant="light" color={SI_A_COLOR(a.value)}>
                          {(SI_A_LABEL[a.value] || a.value)}: {a.count} ({a.pct}%)
                        </Badge>
                      ))}
                    </Group>
                  </div>
                ))}
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}
    </Card>
  );
}

/** Section: WhatsApp community join link (Config "community_join") — powers the in-app "Join" button. */
function CommunityJoin() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [link, setLink] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    loadConfig("community_join").then((v) => {
      if (v) {
        setEnabled(v.enabled === true);
        setLink(v.link || "");
        setMessage(v.message || "");
      }
      setLoading(false);
    });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await saveConfig(
        "community_join",
        { enabled, link: link.trim(), message: message.trim() },
        "community",
      );
      notifications.show({ title: "Saved", message: "Community join link updated", color: "green" });
    } catch {
      notifications.show({ title: "Error", message: "Could not save", color: "red" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card withBorder radius="md" p="lg">
      <Group gap="xs" mb="xs">
        <IconMessageQuestion size={20} color="var(--mantine-color-green-6)" />
        <Title order={4}>WhatsApp community link</Title>
        <Badge color="green" variant="light">onboarding</Badge>
      </Group>
      <Text c="dimmed" size="sm" mb="md">
        The invite link the in-app "Join Community" button opens. WhatsApp invite links reset, so
        paste the current one here and update it whenever it changes. Use the switch to turn the
        button on or off — it fails safe (off) if the link is blank.
      </Text>
      {loading ? (
        <Loader size="sm" />
      ) : (
        <Stack gap="sm">
          <Switch
            label="Show the Join Community button in the app"
            checked={enabled}
            onChange={(e) => setEnabled(e.currentTarget.checked)}
          />
          <TextInput
            label="WhatsApp invite link"
            placeholder="https://chat.whatsapp.com/..."
            value={link}
            onChange={(e) => setLink(e.currentTarget.value)}
          />
          <TextInput
            label="Button message (optional)"
            placeholder="Join the VIREL community on WhatsApp 💚"
            value={message}
            onChange={(e) => setMessage(e.currentTarget.value)}
          />
          <Group>
            <Button loading={saving} onClick={save}>Save</Button>
          </Group>
        </Stack>
      )}
    </Card>
  );
}

export default function ControlCenter() {
  const { admin } = useAuth();
  const email = String((admin as any)?.email || "").toLowerCase();
  if (!ALLOW.includes(email)) {
    return (
      <Container py="xl">
        <Alert color="red" title="No access">This area is restricted.</Alert>
      </Container>
    );
  }

  return (
    <Container size="lg" py="md">
      <Group gap="xs" mb={4}>
        <IconAdjustments size={26} />
        <Title order={2}>Control Center</Title>
        <Badge color="orange" variant="light">Anointed & Favour only</Badge>
      </Group>
      <Text c="dimmed" size="sm" mb="lg">
        Tune the platform from here instead of changing code. More controls (daily reward cap,
        verification thresholds, enforcement switches) are being added — see ADMIN-CONTROL-CENTER.md.
      </Text>
      <Stack gap="lg">
        <CommunityJoin />
        <FrozenAccounts />
        <EnforcementModes />
        <VerificationStandards />
        <FreshClock />
        <DailyCap />
        <SurveyQuestions />
        <SurveyInsights />
        <ListeningSlots />
        <AppVersion />
      </Stack>
    </Container>
  );
}
